"use client"

import * as React from "react"
import { cn } from "@/lib/utils"
import { CheckIcon, ChevronRightIcon } from "lucide-react"
import { DropdownMenu as DropdownMenuPrimitive } from "radix-ui"

import { Kbd } from "@/components/ui/kbd"
import { floatingSurfaceVariants } from "@/components/ui/popover"

type MenuSize = "sm" | "md" | "lg"

// The one source for list popups: dropdown-menu, context-menu and menubar
// use it whole; select, combobox and command build their items, labels,
// separators and empty states from it. Items read their height from the
// nearest `group/menu` surface's data-size (28px by default, 32px at md),
// which sub-menus inherit because Radix renders them inside it.
const menuItemBase =
  "relative flex min-h-control-sm cursor-default items-center gap-2 rounded-sm px-2 py-1 text-ui outline-hidden focus-ring-inset transition-colors duration-fast ease-standard select-none group-data-[size=lg]/menu:min-h-control-lg group-data-[size=md]/menu:min-h-control-md data-highlighted:bg-accent data-highlighted:text-accent-foreground data-inset:pl-8 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground"
const menuItemDisabled =
  "data-disabled:pointer-events-none data-disabled:text-muted-foreground"

const menuStyles = {
  content: cn(
    floatingSurfaceVariants({ kind: "list" }),
    "group/menu min-w-48 overflow-x-hidden overflow-y-auto",
  ),
  // cmdk writes data-disabled="false" on every item, so command builds on
  // itemBase and keys disabled off data-[disabled=true] instead.
  itemBase: menuItemBase,
  item: cn(menuItemBase, menuItemDisabled),
  // Combobox and command keep DOM focus in their input and mark the active
  // row with data-highlighted or data-selected, so :focus-visible never
  // fires on the row. Draw the same inset ring from that state instead.
  virtualFocus:
    "data-highlighted:outline-2 data-highlighted:-outline-offset-2 data-highlighted:outline-ring data-highlighted:outline-solid data-[selected=true]:outline-2 data-[selected=true]:-outline-offset-2 data-[selected=true]:outline-ring data-[selected=true]:outline-solid",
  destructive:
    "data-[variant=destructive]:text-destructive data-[variant=destructive]:data-highlighted:bg-destructive-bg data-[variant=destructive]:data-highlighted:text-destructive data-[variant=destructive]:*:[svg]:text-destructive!",
  checkable: "pl-8",
  indicator:
    "pointer-events-none absolute left-2 flex size-4 items-center justify-center text-primary",
  // Select and combobox put the check at the end, as native selects do.
  checkableEnd: "pr-8",
  indicatorEnd:
    "pointer-events-none absolute right-2 flex size-4 items-center justify-center text-primary",
  radioDot: "block size-1.5 rounded-full bg-current",
  label: "px-2 pt-2 pb-1 text-eyebrow text-muted-foreground data-inset:pl-8",
  separator: "pointer-events-none -mx-1 my-1 h-px bg-border",
  empty: "px-2 py-3 text-center text-ui text-muted-foreground",
  shortcut: "ml-auto pl-4",
  subTrigger:
    "data-[state=open]:bg-accent data-[state=open]:text-accent-foreground",
  subTriggerIcon: "ml-auto size-4",
}

function MenuShortcut({
  className,
  ...props
}: React.ComponentProps<typeof Kbd>) {
  return <Kbd className={cn(menuStyles.shortcut, className)} {...props} />
}

function DropdownMenu({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Root>) {
  return <DropdownMenuPrimitive.Root data-slot="dropdown-menu" {...props} />
}

function DropdownMenuPortal({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Portal>) {
  return (
    <DropdownMenuPrimitive.Portal data-slot="dropdown-menu-portal" {...props} />
  )
}

function DropdownMenuTrigger({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Trigger>) {
  return (
    <DropdownMenuPrimitive.Trigger
      data-slot="dropdown-menu-trigger"
      {...props}
    />
  )
}

function DropdownMenuContent({
  className,
  sideOffset = 4,
  size = "sm",
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Content> & {
  /** Row height: sm 28px (default), md 32px, lg 36px. */
  size?: MenuSize
}) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        data-slot="dropdown-menu-content"
        data-size={size}
        sideOffset={sideOffset}
        className={cn(
          menuStyles.content,
          "max-h-(--radix-dropdown-menu-content-available-height) origin-(--radix-dropdown-menu-content-transform-origin)",
          className,
        )}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  )
}

function DropdownMenuGroup({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Group>) {
  return (
    <DropdownMenuPrimitive.Group data-slot="dropdown-menu-group" {...props} />
  )
}

function DropdownMenuItem({
  className,
  inset,
  variant = "default",
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Item> & {
  inset?: boolean
  variant?: "default" | "destructive"
}) {
  return (
    <DropdownMenuPrimitive.Item
      data-slot="dropdown-menu-item"
      data-inset={inset}
      data-variant={variant}
      className={cn(menuStyles.item, menuStyles.destructive, className)}
      {...props}
    />
  )
}

function DropdownMenuCheckboxItem({
  className,
  children,
  checked,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.CheckboxItem>) {
  return (
    <DropdownMenuPrimitive.CheckboxItem
      data-slot="dropdown-menu-checkbox-item"
      className={cn(menuStyles.item, menuStyles.checkable, className)}
      checked={checked}
      {...props}
    >
      <span className={menuStyles.indicator}>
        <DropdownMenuPrimitive.ItemIndicator>
          <CheckIcon className="size-4 text-primary" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  )
}

function DropdownMenuRadioGroup({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.RadioGroup>) {
  return (
    <DropdownMenuPrimitive.RadioGroup
      data-slot="dropdown-menu-radio-group"
      {...props}
    />
  )
}

function DropdownMenuRadioItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.RadioItem>) {
  return (
    <DropdownMenuPrimitive.RadioItem
      data-slot="dropdown-menu-radio-item"
      className={cn(menuStyles.item, menuStyles.checkable, className)}
      {...props}
    >
      <span className={menuStyles.indicator}>
        <DropdownMenuPrimitive.ItemIndicator>
          <span className={menuStyles.radioDot} />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.RadioItem>
  )
}

function DropdownMenuLabel({
  className,
  inset,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Label> & {
  inset?: boolean
}) {
  return (
    <DropdownMenuPrimitive.Label
      data-slot="dropdown-menu-label"
      data-inset={inset}
      className={cn(menuStyles.label, className)}
      {...props}
    />
  )
}

function DropdownMenuSeparator({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Separator>) {
  return (
    <DropdownMenuPrimitive.Separator
      data-slot="dropdown-menu-separator"
      className={cn(menuStyles.separator, className)}
      {...props}
    />
  )
}

function DropdownMenuShortcut(props: React.ComponentProps<typeof Kbd>) {
  return <MenuShortcut data-slot="dropdown-menu-shortcut" {...props} />
}

function DropdownMenuSub({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Sub>) {
  return <DropdownMenuPrimitive.Sub data-slot="dropdown-menu-sub" {...props} />
}

function DropdownMenuSubTrigger({
  className,
  inset,
  children,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubTrigger> & {
  inset?: boolean
}) {
  return (
    <DropdownMenuPrimitive.SubTrigger
      data-slot="dropdown-menu-sub-trigger"
      data-inset={inset}
      className={cn(menuStyles.item, menuStyles.subTrigger, className)}
      {...props}
    >
      {children}
      <ChevronRightIcon className={menuStyles.subTriggerIcon} />
    </DropdownMenuPrimitive.SubTrigger>
  )
}

function DropdownMenuSubContent({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubContent>) {
  return (
    <DropdownMenuPrimitive.SubContent
      data-slot="dropdown-menu-sub-content"
      className={cn(
        menuStyles.content,
        "origin-(--radix-dropdown-menu-content-transform-origin)",
        className,
      )}
      {...props}
    />
  )
}

export {
  DropdownMenu,
  DropdownMenuPortal,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  MenuShortcut,
  menuStyles,
  type MenuSize,
}
